import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import GLOBALS from '../config/globals';
import { getAutoManagerConfig } from '../helpers/autoManagerHelpers';
import { translateEvent, translatePitchRoll, buildTickerText } from '../utils/gameUtils';
import { makeRunner, deriveResultState } from '../helpers/gamePlayHelpers';
import { cleanTeamName } from '../helpers/teamHelpers';

import Card from './Card';
import GameLineup from './GameLineup';
import SpecialGameOverlay from './SpecialGameOverlay';
import RunnersDisplay from './RunnersDisplay';
import CompactScoreboard from './CompactScoreboard';
import GameResultPopup from './GameResultPopup';
import DiceAnimation from './DiceAnimation';
import PlayByPlayReveal from './PlayByPlayReveal';

const cpuRobotOverlay = <FontAwesomeIcon icon={faRobot} className="cpu-robot-overlay" aria-label="CPU-controlled opponent" />;

const DefaultGameLayout = ({
    game, participants, gameTheme, gameSpeed,
    engine, pitcherFatigue,
    autoPlay, managerMode,
    isManagerOpen, setIsManagerOpen, isParticipantsOpen,
    setIsParticipantsOpen,
    handleRollClick, toggleManager, subMenu, handleAutoCycle,
}) => {
    const { revealPhase, userIsPitching, displayUserIsPitching, currentBatterCard, currentPitcherCard,
        rolling, managedEventPopup, eventUserIsPitchingRef, displayEvent, inningOverPopup } = engine;
    const { currentPitcherEffectiveStamina, pitcherWorkMapRef } = pitcherFatigue;

    const latestEvent = game.GameEvents?.[0] || {};
    const normalizedSpecialType = game.specialGameType?.toLowerCase() || '';
    const specialGameClass = GLOBALS.SPECIAL_GAME_BG[normalizedSpecialType] ?? '';
    const cpuVariation = normalizedSpecialType
        ? { name: normalizedSpecialType, cssClass: `variation-${normalizedSpecialType}` }
        : null;
    const homeParticipant = participants?.find(p => p.role === 'home');
    const awayParticipant = participants?.find(p => p.role === 'away');

    const nextPitcherWork = game.nextPitcherWork ?? 0;
    const pitcherWorkMap = pitcherWorkMapRef.current;
    const buttonLabel = userIsPitching ? 'Hurl' : 'Swing';

    const runnerSource = displayEvent || latestEvent;
    const firstRunner = makeRunner(runnerSource.firstBaseRunnerLineup, runnerSource.firstBaseRunnerLineupID, displayUserIsPitching);
    const secondRunner = makeRunner(runnerSource.secondBaseRunnerLineup, runnerSource.secondBaseRunnerLineupID, displayUserIsPitching);
    const thirdRunner = makeRunner(runnerSource.thirdBaseRunnerLineup, runnerSource.thirdBaseRunnerLineupID, displayUserIsPitching);

    if (normalizedSpecialType) {
        const cpuVariationCss = `variation-${normalizedSpecialType}`;
        [firstRunner, secondRunner, thirdRunner].forEach(runner => {
            if (runner && !runner.isUser) {
                runner.variationCss = cpuVariationCss;
            }
        });
    }

    const {
        strikeThroughH, strikeThroughP, runnersOnBase, showResultPopup,
        resultOnPitcherCard, resultIsGoodForUser, resultIsXBH,
        resultPopupText, eventWasModified, origPopupText,
    } = deriveResultState(revealPhase, latestEvent, userIsPitching, eventUserIsPitchingRef);

    const showSteal = !userIsPitching && runnersOnBase;
    const outsInHalf = (latestEvent.ipOuts ?? 0) % 6 % 3;
    const showBunt = !userIsPitching && runnersOnBase && outsInHalf < 2;

    return (
        <div className={`play-games-page ${specialGameClass}`.trim()}>
            <SpecialGameOverlay specialGameType={game?.specialGameType} />
            <div>
                <div className="game-header-new">
                    <RunnersDisplay
                        firstBase={firstRunner}
                        secondBase={secondRunner}
                        thirdBase={thirdRunner}
                        theme={gameTheme}
                    />
                    <CompactScoreboard
                        awayTeam={cleanTeamName(awayParticipant?.UserTeam?.name)}
                        homeTeam={cleanTeamName(homeParticipant?.UserTeam?.name)}
                        awayR={runnerSource.visitingScore ?? game.scoreboardData?.visitorScore ?? 0}
                        awayH={game.scoreboardData?.visitorHits ?? 0}
                        awayE={game.scoreboardData?.visitorErrors ?? 0}
                        homeR={runnerSource.homeScore ?? game.scoreboardData?.homeScore ?? 0}
                        homeH={game.scoreboardData?.homeHits ?? 0}
                        homeE={game.scoreboardData?.homeErrors ?? 0}
                        ipOuts={runnerSource.ipOuts ?? 0}
                        isFinal={!!game.isFinal}
                    />
                </div>
                <div className="game-player-area">
                <div className="lineups">
                    {' '}
                    <div className="lineup">
                        {currentBatterCard && (
                            <div className={`card-popup-wrapper${displayUserIsPitching ? ' cpu-side' : ''}`}>
                                <Card
                                    card={currentBatterCard}
                                    diceResultH={revealPhase && !['pending', 'done'].includes(revealPhase) ? latestEvent.diceResultH : null}
                                    diceResultX={revealPhase && !['pending', 'done'].includes(revealPhase) ? latestEvent.diceResultX : null}
                                    variation={displayUserIsPitching ? cpuVariation : null}
                                    frontOverlay={displayUserIsPitching ? cpuRobotOverlay : null}
                                />
                            </div>
                        )}
                    </div>
                    <div className="lineup">
                        {currentPitcherCard && (
                            <div className={`card-popup-wrapper${!displayUserIsPitching ? ' cpu-side' : ''}${currentPitcherCard && nextPitcherWork >= currentPitcherEffectiveStamina ? ' pitcher-fatigued' : ''}`}>
                                <Card
                                    card={currentPitcherCard}
                                    pitcherWork={nextPitcherWork}
                                    diceResultP={revealPhase && !['pending', 'batting', 'done'].includes(revealPhase) ? latestEvent.diceResultP : null}
                                    variation={displayUserIsPitching ? null : cpuVariation}
                                    frontOverlay={!displayUserIsPitching ? cpuRobotOverlay : null}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <GameResultPopup
                    showResultPopup={showResultPopup}
                    resultOnPitcherCard={resultOnPitcherCard}
                    resultIsGoodForUser={resultIsGoodForUser}
                    resultIsXBH={resultIsXBH}
                    eventWasModified={eventWasModified}
                    origPopupText={origPopupText}
                    resultPopupText={resultPopupText}
                    managedEventPopup={managedEventPopup}
                />
                {inningOverPopup && (
                    <div className="inning-over-popup">Inning Over</div>
                )}
                <div className="game-info">
                    {game.isFinal ? (
                        <span className="final-summary">
                            Final: <Link to={`/game/${game.gameID}/summary`} className="button">Game Summary</Link>
                        </span>
                    ) : (
                        <>
                            <button className="game-btn" onClick={toggleManager}>
                                <span className="label-full">Calls</span><span className="label-short">Calls</span>
                            </button>
                            <button
                                className={`game-btn${autoPlay ? ' game-btn--active' : ''}${managerMode ? ` ${GLOBALS.AUTO_MANAGER.CSS[managerMode]}` : ''}`}
                                onClick={handleAutoCycle}
                            >
                                {!autoPlay && 'Auto'}
                                {autoPlay && !managerMode && 'Auto'}
                                {autoPlay && managerMode && (
                                    <>
                                        <span className="label-full">Auto: {getAutoManagerConfig(managerMode)?.label}</span>
                                        <span className="label-short">Auto: {getAutoManagerConfig(managerMode)?.mobileLabel}</span>
                                    </>
                                )}
                            </button>
                            <button className="game-btn game-btn--cta" onClick={handleRollClick} disabled={rolling}>
                                {rolling ? <><span className="label-full">Rolling...</span><span className="label-short">...</span></> : buttonLabel}
                            </button>
                        </>
                    )}
                </div>
                </div>
                {isManagerOpen && (
                    <div className="accordion-content">
                        <h3>Play Calls</h3>
                        {userIsPitching ? (
                            <button onClick={engine.handleManagedWalk}>Intentional Walk</button>
                        ) : (
                            <button className="inactive">Intentional Walk</button>
                        )}
                        <button onClick={subMenu}>Substitute</button>

                        {showSteal ? (
                            <button onClick={() => { setIsManagerOpen(false); engine.setIsStealModalOpen(true); }}>Steal</button>
                        ) : (
                            <button className="inactive">Steal</button>
                        )}

                        {showBunt ? (
                            <button onClick={() => { setIsManagerOpen(false); engine.handleManagedBunt(); }}>Sacrifice Bunt</button>
                        ) : (
                            <button className="inactive">Sacrifice Bunt</button>
                        )}
                    </div>
                )}
                {revealPhase && (
                    <div className="at-bat-result-panel">
                        {revealPhase !== 'pending' && (<>
                            <div className="result-dice-row">
                                <div className="result-dice-group">
                                    <span className="result-dice-label"><span className="label-full">Batting</span><span className="label-short">B:</span></span>
                                    <DiceAnimation
                                        diceResultH={latestEvent.diceResultX ? undefined : latestEvent.diceResultH}
                                        diceResultX={latestEvent.diceResultX}
                                        gameSpeed={gameSpeed}
                                    />
                                    <span className={`result-box${strikeThroughH ? ' strike-through' : ''}`}>
                                        <span className="label-full">{translateEvent(latestEvent.origResultType).event}</span>
                                        <span className="label-short">{translateEvent(latestEvent.origResultType).smallEvent}</span>
                                    </span>
                                </div>
                                {revealPhase !== 'batting' && (
                                    <div className="result-dice-group">
                                        <span className="result-dice-label"><span className="label-full">Pitching</span><span className="label-short">P:</span></span>
                                        <DiceAnimation
                                            diceResultP={latestEvent.diceResultP}
                                            gameSpeed={gameSpeed}
                                        />
                                        <span
                                            className={`result-box${strikeThroughP ? ' strike-through' : ''}${
                                                latestEvent?.diceModifiers?.pitcher?.includes('fatigue') &&
                                                latestEvent?.origDiceResults?.pitcher &&
                                                latestEvent?.diceResults?.pitcher &&
                                                latestEvent.origDiceResults.pitcher !== latestEvent.diceResults.pitcher
                                                    ? ' fatigue-modified' : ''
                                            }`}
                                            title={
                                                latestEvent?.diceModifiers?.pitcher?.includes('fatigue') &&
                                                latestEvent?.origDiceResults?.pitcher &&
                                                latestEvent?.diceResults?.pitcher &&
                                                latestEvent.origDiceResults.pitcher !== latestEvent.diceResults.pitcher
                                                    ? `Pitcher fatigued: ${latestEvent.origDiceResults.pitcher} → ${latestEvent.diceResults.pitcher}`
                                                    : undefined
                                            }
                                        >
                                            <span className="label-full">{translatePitchRoll(latestEvent.pitchResultType).result}</span>
                                            <span className="label-short">{translatePitchRoll(latestEvent.pitchResultType).smallResult}</span>
                                        </span>
                                    </div>
                                )}
                            </div>

                            {['final', 'description', 'done'].includes(revealPhase) && (() => {
                                const tickerText = buildTickerText(latestEvent);
                                const tickerCount = Math.max(40, Math.ceil(120 / (tickerText?.length || 5)));
                                return (
                                <div className="result-final-box">
                                    <div className="result-final-ticker">
                                        {Array.from({ length: tickerCount }, (_, i) => (
                                            <span
                                                key={i}
                                                className="result-final-label"
                                                style={{ animationDelay: `${i * 80}ms` }}
                                            >
                                                {tickerText}
                                            </span>
                                        ))}
                                    </div>
                                    {['description', 'done'].includes(revealPhase) && (latestEvent.playByPlay?.length || latestEvent.resultDescription) && (
                                        <div className="result-description">
                                            <PlayByPlayReveal
                                                lines={latestEvent.playByPlay?.length ? latestEvent.playByPlay : latestEvent.resultDescription ? [latestEvent.resultDescription] : []}
                                                gameSpeed={gameSpeed}
                                            />
                                        </div>
                                    )}
                                </div>
                                );
                            })()}
                        </>)}
                    </div>
                )}
                <div className="participants-section">
                    <h3 onClick={() => setIsParticipantsOpen(prev => !prev)} className="participants-header">
                        Participants
                    </h3>

                    {isParticipantsOpen && (
                        <div className="participants-container">
                            {participants?.map(participant => (
                                <GameLineup
                                    key={participant.gameParticipantID}
                                    participant={participant}
                                    latestEvent={latestEvent}
                                    pitcherWorkMap={participant.userID ? pitcherWorkMap : undefined}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DefaultGameLayout;
